import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { useTranslation } from 'helpers/hooks';
import { CloseButton } from 'common/CloseButton';

import { renderRidePrice, Ride } from '../../../../types/ride';
import { setSelectedObject } from '../../../../store/map/actions';
import { getPrintTime } from '../../../../core/rides/helpers';
import { Button } from '../../../styled/form';
import { CopyAddressButton } from '../../CopyAddressButton';
import { Price, RoundTrip, Time, Type } from '../styled';
import { registerRidesSliderClickEvent } from '../../../../services/reactGa';
import { AppState } from '../../../../store/interfaces';
import { RideType } from '../../../RideType';
import { dropRide } from '../../../../store/creditsHistory/actions';

import { ReactComponent as ScrollLeft } from './scroll_left.svg';
import { ReactComponent as ScrollRight } from './scroll_right.svg';

interface GetRidesNavigationsValue {
  prevRide: Ride | null;
  nextRide: Ride | null;
  currentRide: number | null;
}

const Container = styled.section`
  .rides-controls {
    display: flex;
    flex-direction: row;

    font-size: 14px;

    .rides-buttons {
      margin-left: auto;
      display: flex;
      flex-direction: row;
      align-items: center;

      &__total {
        margin: 0 8px;
        font-size: 16px;
        font-weight: bold;
        width: 48px;
        display: flex;
        justify-content: center;
        align-content: center;
        letter-spacing: -0.6px;
      }

      &__scroll {
        max-height: 22px;

        svg {
          width: 100%;
          height: 100%;
          fill: #808184;
        }

        &--active {
          svg {
            fill: #fc00eb;
          }
        }
      }

      span {
        width: 12px;
        height: 22px;
        display: inline-block;
      }
    }
  }

  .ride-title {
    display: flex;
    flex-direction: row;
    align-items: center;

    > span {
      margin-right: 16px;

      &:last-child {
        margin-right: 0;
        margin-left: auto;
      }
    }

    margin: 16px 0 4px;
    padding-right: 10px;
    justify-content: space-between;
  }

  .ride-start {
    color: ${(props) => props.theme.map.ride.start};
  }

  .ride-finish {
    color: ${(props) => props.theme.map.ride.finish};
  }
`;

const getRidesNavigations = (
  rides: Ride[] | undefined,
  currentRideId: number
): GetRidesNavigationsValue => {
  const defaultValue: GetRidesNavigationsValue = {
    prevRide: null,
    nextRide: null,
    currentRide: null,
  };

  if (!rides || rides.length === 0) {
    return defaultValue;
  }

  return rides.reduce((acc, ride, index) => {
    if (ride.id === currentRideId) {
      return {
        prevRide: rides[index - 1] ? rides[index - 1] : null,
        nextRide: rides[index + 1] ? rides[index + 1] : null,
        currentRide: index + 1,
      };
    }

    return acc;
  }, defaultValue);
};

interface RideDetailsProps {
  selectedObject: Ride;
  rides?: Ride[];
  onCancel(): void;
}

export const RideDetails: React.FC<RideDetailsProps> = function ({
  rides,
  selectedObject,
  onCancel,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const droppingSelectedRide = useSelector(
    (app: AppState) => app.creditsHistory.droppingRides[selectedObject.id]
  );

  const isLoadingDropRide = droppingSelectedRide && droppingSelectedRide?.code === undefined;

  const allowedSkipButton =
    // @ts-ignore
    selectedObject.pickup_time < +new Date() && selectedObject.ride_status >= 0;

  const { prevRide, nextRide, currentRide } = getRidesNavigations(rides, selectedObject.id);
  const selectRide = useCallback((ride: Ride | null) => {
    if (!ride) {
      return;
    }

    registerRidesSliderClickEvent();
    dispatch(setSelectedObject(ride));
  }, []);

  const handleDropRide = () => {
    dispatch(dropRide.request(selectedObject));
  };

  return (
    <Container>
      <div className="ride-title">
        <Price>
          {renderRidePrice(selectedObject)} /{' '}
          {selectedObject.distance ? selectedObject.distance : <RoundTrip>??</RoundTrip>} Mi
        </Price>
        <Time>
          <span className="ride-start">{getPrintTime(selectedObject.pickup_time)}</span>
          <span>{' - '}</span>
          <span className="ride-finish">{getPrintTime(selectedObject.end_time)}</span>
        </Time>
        <span />
        {/* <CloseButton right={8} top={14} size="small" onClick={onCancel} /> */}
      </div>
      <Type>
        <RideType ride={selectedObject} withSpaceReplace={false} />
      </Type>
      <p className="ride-start address">
        {t('map.pickUp')}: {selectedObject.start_address}
      </p>
      <p className="ride-finish address">
        {selectedObject.start_address === selectedObject.end_address ? (
          t('rides.roundTrip')
        ) : (
          <>
            {t('map.dropOff')}: {selectedObject.end_address}
          </>
        )}
      </p>
      <div className="rides-controls">
        {allowedSkipButton && (
          <Button
            isLoading={isLoadingDropRide}
            className="button-sm button-skip"
            onClick={handleDropRide}
          >
            {t('map.skip')}
          </Button>
        )}
        <CopyAddressButton className="button-sm" address={selectedObject.start_address}>
          {t('map.copy')}
        </CopyAddressButton>
        {(prevRide || nextRide) && (
          <div className="rides-buttons">
            <span
              className={`rides-buttons__scroll ${prevRide ? 'rides-buttons__scroll--active' : ''}`}
            >
              <ScrollLeft onClick={() => selectRide(prevRide)} />
            </span>

            <div className="rides-buttons__total">
              {currentRide} / {rides?.length}
            </div>
            <span
              className={`rides-buttons__scroll ${nextRide ? 'rides-buttons__scroll--active' : ''}`}
            >
              <ScrollRight onClick={() => selectRide(nextRide)} />
            </span>
          </div>
        )}
      </div>
    </Container>
  );
};
